import { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Eye, Search } from "lucide-react";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOURCE_LABELS = {
  SPECIAL: { text: "Especial", cls: "bg-amber-100 text-amber-800" },
  BRANCH: { text: "Sede", cls: "bg-indigo-100 text-indigo-800" },
  BASE: { text: "Base", cls: "bg-slate-100 text-slate-700" },
  NONE: { text: "Consultar", cls: "bg-red-100 text-red-700" },
};

export default function PricePreview() {
  const [clients, setClients] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/admin/users`, { params: { limit: 500 } })
      .then((r) => setClients(r.data || []))
      .catch(() => toast.error("Error cargando clientes"));
  }, []);

  useEffect(() => {
    if (!selectedUserId) { setPreview(null); return; }
    setLoading(true);
    axios
      .get(`${API}/admin/prices/preview`, { params: { user_id: selectedUserId } })
      .then((r) => setPreview(r.data))
      .catch(() => toast.error("Error cargando vista previa"))
      .finally(() => setLoading(false));
  }, [selectedUserId]);

  const filtered = preview?.rows?.filter((r) => {
    if (!searchInput) return true;
    const q = searchInput.toLowerCase();
    return (r.name || "").toLowerCase().includes(q) || (r.code || "").toLowerCase().includes(q);
  });

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="price-preview-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center">
            <Eye className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Verificar Precios</h1>
            <p className="text-slate-500">Vista de solo lectura: precio que ve cada cliente</p>
          </div>
        </div>

        {/* Client selector */}
        <Card>
          <CardContent className="p-4">
            <label className="text-sm font-medium text-slate-700 mb-2 block">Selecciona un cliente</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="w-full max-w-md h-10 px-3 border border-slate-200 rounded-md text-sm"
              data-testid="preview-client-select"
            >
              <option value="">-- Seleccionar --</option>
              {clients.map((c) => (
                <option key={c.user_id} value={c.user_id}>
                  {c.name} {c.company ? `(${c.company})` : ""} {c.branch ? `[${c.branch}]` : ""} {c.erp_customer_code ? `A3:${c.erp_customer_code}` : ""}
                </option>
              ))}
            </select>
          </CardContent>
        </Card>

        {/* User info */}
        {preview?.user && (
          <div className="flex flex-wrap gap-3 text-sm">
            <span className="px-3 py-1 bg-slate-100 rounded-md">
              <strong>Sede:</strong> {preview.user.branch || <span className="text-red-500">Sin asignar</span>}
            </span>
            <span className="px-3 py-1 bg-slate-100 rounded-md">
              <strong>Código A3:</strong> {preview.user.erp_customer_code || <span className="text-red-500">Sin asignar</span>}
            </span>
          </div>
        )}

        {/* Search */}
        {preview?.rows?.length > 0 && (
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Filtrar por nombre o código..."
              className="pl-10"
              data-testid="preview-search"
            />
          </div>
        )}

        {/* Results table */}
        {loading ? (
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
          </div>
        ) : preview && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {filtered?.length || 0} productos publicados y asignados
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!filtered?.length ? (
                <p className="text-center py-8 text-slate-500">
                  Este cliente no tiene productos publicados asignados
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Código</TableHead>
                        <TableHead>Producto</TableHead>
                        <TableHead>Origen</TableHead>
                        <TableHead className="text-right">Precio resuelto</TableHead>
                        <TableHead className="text-right">Dto %</TableHead>
                        <TableHead className="text-right">Precio final</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((r) => {
                        const src = SOURCE_LABELS[r.price_source] || SOURCE_LABELS.NONE;
                        return (
                          <TableRow key={r.product_id} data-testid={`preview-row-${r.product_id}`}>
                            <TableCell className="font-mono text-sm text-slate-600">{r.code || "-"}</TableCell>
                            <TableCell className="font-medium">{r.name}</TableCell>
                            <TableCell>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${src.cls}`}>
                                {src.text}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              {r.price_resolved != null ? `${r.price_resolved.toFixed(2)}€` : "-"}
                            </TableCell>
                            <TableCell className="text-right">
                              {r.discount > 0 ? <span className="text-green-600">-{r.discount}%</span> : "-"}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {r.price_source === "NONE" ? (
                                <span className="text-amber-600">Consultar</span>
                              ) : (
                                `${(r.price_applied ?? r.price_resolved ?? 0).toFixed(2)}€`
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
}
